/**
 * MLBLeadersWidget — season leader graphics for teams and players
 *
 * Reads the graphics the Space renders daily. One image `source` per group
 * (mlb-leaders-<scope>-<group>), which is also how they stay under
 * MAX_IMAGES_PER_SOURCE=150 in discord_post_logger.
 *
 * Two things this widget must handle that others do not:
 *
 *  1. THE SPACE FILESYSTEM IS EPHEMERAL. data/images is wiped on every deploy,
 *     sleep/wake and crash-restart, so an empty source early in the day is
 *     NORMAL, not an error. All six groups are swept daily at 09:00 ET; before
 *     that sweep completes, counts legitimately read zero.
 *  2. CANVAS WIDTH IS DATA-DEPENDENT. A long player name resizes the whole
 *     poster (2416 -> 2888px was observed from one hyphenated surname), so
 *     nothing here may assume a fixed aspect ratio.
 *
 * Refresh: 120s | CSS prefix: mlw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFromProxy, imageUrl } from '../lib/api'
import { Trophy, RefreshCw, AlertCircle, Users, Shield, ChevronDown } from 'lucide-react'
import './MLBLeadersWidget.css'

const GROUPS = [
  { id: 'team-hitting',     scope: 'team',   group: 'hitting',  label: 'Team Hitting' },
  { id: 'team-pitching',    scope: 'team',   group: 'pitching', label: 'Team Pitching' },
  { id: 'team-fielding',    scope: 'team',   group: 'fielding', label: 'Team Fielding' },
  { id: 'player-hitting',   scope: 'player', group: 'hitting',  label: 'Player Hitting' },
  { id: 'player-pitching',  scope: 'player', group: 'pitching', label: 'Player Pitching' },
  { id: 'player-fielding',  scope: 'player', group: 'fielding', label: 'Player Fielding' },
]

const REFRESH_MS = 120000

/** "20260821_211946_wildPitches.png" -> "wildPitches"; plain names pass through */
function categoryOf(path) {
  const file = (path || '').split('/').pop() || ''
  const m = file.match(/^\d{8}_\d{6}_(.+)\.png$/)
  return m ? m[1] : file.replace(/\.png$/, '')
}

function prettify(cat) {
  return cat
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
}

export default function MLBLeadersWidget() {
  const [active, setActive] = useState(GROUPS[0].id)
  const [images, setImages] = useState([])
  const [status, setStatus] = useState('loading')
  const [message, setMessage] = useState(null)
  const [counts, setCounts] = useState({})
  const [open, setOpen] = useState(null)
  const [updated, setUpdated] = useState(null)

  const load = useCallback(async () => {
    // One call PER SOURCE. A single unfiltered /images fetch returns a
    // newest-first window, and odds/lineups post all day, so any fixed limit
    // eventually pushes that dawn's leaders renders past the cut — limit=400
    // hid every team group once the catalog passed 400 files. Per-source
    // requests cannot be crowded out, whatever the other monitors do.
    const results = await Promise.all(
      GROUPS.map((g) => fetchFromProxy('/images', { source: `mlb-leaders-${g.id}`, limit: 200 }))
    )
    const bad = results.find((r) => r.status !== 'ok')
    if (bad) {
      setStatus(bad.status)
      setMessage(bad.message)
      return
    }
    const byGroup = {}
    const perGroup = {}
    GROUPS.forEach((g) => { byGroup[g.id] = 0 })
    results.forEach((r, idx) => {
      const g = GROUPS[idx].id
      const list = Array.isArray(r.data) ? r.data : (r.data?.images || [])
      byGroup[g] = list.length
      perGroup[g] = list
    })
    setCounts(byGroup)
    setImages(perGroup[active] || [])
    setStatus('ok')
    setMessage(null)
    setUpdated(new Date())
  }, [active])

  useEffect(() => {
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load])

  const total = Object.values(counts).reduce((a, b) => a + b, 0)

  return (
    <div className="mlw-root">
      <header className="mlw-head">
        <div className="mlw-title">
          <Trophy size={16} />
          <span>MLB Season Leaders</span>
        </div>
        <div className="mlw-meta">
          {total > 0 && <span className="mlw-count">{total} graphics</span>}
          {updated && <span className="mlw-ts">{updated.toLocaleTimeString()}</span>}
          <button className="mlw-refresh" onClick={load} aria-label="Refresh">
            <RefreshCw size={13} />
          </button>
        </div>
      </header>

      <nav className="mlw-tabs" role="tablist">
        {GROUPS.map((g) => {
          const n = counts[g.id] ?? 0
          return (
            <button
              key={g.id}
              role="tab"
              aria-selected={active === g.id}
              className={`mlw-tab ${active === g.id ? 'is-active' : ''} ${n === 0 ? 'is-empty' : ''}`}
              onClick={() => setActive(g.id)}
            >
              {g.scope === 'team' ? <Shield size={12} /> : <Users size={12} />}
              <span>{g.label}</span>
              <span className="mlw-badge">{n}</span>
            </button>
          )
        })}
      </nav>

      {status === 'loading' && <div className="mlw-note">Loading…</div>}

      {status !== 'ok' && status !== 'loading' && (
        <div className="mlw-note mlw-err">
          <AlertCircle size={14} />
          <span>{message || 'Could not reach the monitor proxy'}</span>
        </div>
      )}

      {status === 'ok' && images.length === 0 && (
        <div className="mlw-note">
          <AlertCircle size={14} />
          <span>
            No graphics for this group yet. The Space sweeps <strong>all six
            groups</strong> daily at 09:00 ET and its filesystem resets on
            restart, so an empty group means today's sweep has not completed.
          </span>
        </div>
      )}

      <div className="mlw-grid">
        {images.map((im) => {
          const cat = categoryOf(im.path)
          const isOpen = open === im.path
          return (
            <article key={im.path} className={`mlw-item ${isOpen ? 'is-open' : ''}`}>
              <button
                className="mlw-item-head"
                onClick={() => setOpen(isOpen ? null : im.path)}
                aria-expanded={isOpen}
              >
                <ChevronDown size={13} className="mlw-chev" />
                <span className="mlw-cat">{prettify(cat)}</span>
                <span className="mlw-size">
                  {im.size ? `${(im.size / 1e6).toFixed(1)} MB` : ''}
                </span>
              </button>
              {isOpen && (
                <div className="mlw-figure">
                  {/* No fixed aspect: canvas width is data-dependent (one long
                      surname moved a poster 2416 -> 2888px).
                      NOT loading="lazy": the <img> only mounts when the row is
                      expanded, so lazy adds nothing and measurably prevented the
                      load - complete=false / naturalWidth=0 indefinitely, while a
                      direct fetch of the same URL returned 200 with 1,344,655
                      bytes in 1.7s. Eager loads it at 2682x3242. */}
                  <img src={imageUrl(im.path)} alt={`MLB leaders — ${prettify(cat)}`} />
                </div>
              )}
            </article>
          )
        })}
      </div>
    </div>
  )
}
